import { useEffect, useMemo, useState } from 'react'

import { deepEqualWithBigInt } from '@modules/utils/deepEqual'

import type {
  GetDealWithColorSimulationResponse,
  OriginalColorSimulationImages,
} from '@ai-color-modules/gen_proto/api_proto/v1/ai_color_simulation_pb'
import type { ExcludeArea, MergedPolygonsByArea, SubArea } from '@ai-color-modules/simulation/types'
import { filterVisibleSimulatedImages } from '@ai-color-modules/utils/imageFilters'

import { API_TO_AREA_NAME_MAP, extractMergedPolygonsByArea } from '../utils/processParameters'
import { getProcessedImageSelectionState } from '../utils/selectionState'

// ユーザーに見せない画像を除外するヘルパー関数（共通ユーティリティを使用）
const getVisibleSimulatedImages = (image: OriginalColorSimulationImages | undefined) => {
  return filterVisibleSimulatedImages(image?.simulatedImage)
}

interface UseFormParametersSyncOptions {
  images: GetDealWithColorSimulationResponse['images']
  activeTab: string
  selectedProcessedIndexByTab: Record<string, number>
  setAreaColors: React.Dispatch<React.SetStateAction<Record<string, string>>>
  setAreaMaterials: React.Dispatch<React.SetStateAction<Record<string, bigint>>>
  setMergedPolygonsByArea: React.Dispatch<React.SetStateAction<MergedPolygonsByArea>>
  setAreaSelectionMode: React.Dispatch<React.SetStateAction<Record<string, 'color' | 'material'>>>
  setUnifyColor: React.Dispatch<React.SetStateAction<boolean>>

  setSubAreas: (areas: SubArea[]) => void
  setExcludeAreas: (areas: ExcludeArea[]) => void
  setIsSubAreaModeActive: React.Dispatch<React.SetStateAction<boolean>>
  setExcludeEnabled: React.Dispatch<React.SetStateAction<boolean>>
}

interface FormState {
  areaColors: Record<string, string>
  areaMaterials: Record<string, bigint>
  mergedPolygonsByArea: MergedPolygonsByArea
  areaSelectionMode: Record<string, 'color' | 'material'>
  subAreas: SubArea[]
  excludeAreas: ExcludeArea[]
  unifyColor: boolean
}

/**
 * parametersから復元した初期状態を保持し、フォーム変更を検知するhook
 */
export const useFormParametersSync = ({
  images,
  activeTab,
  selectedProcessedIndexByTab,
  setAreaColors,
  setAreaMaterials,
  setMergedPolygonsByArea,
  setAreaSelectionMode,
  setUnifyColor,
  setSubAreas,
  setExcludeAreas,
  setIsSubAreaModeActive,
  setExcludeEnabled,
}: UseFormParametersSyncOptions) => {
  // parametersから復元した初期値を保持（変更検知用）
  const [initialFormState, setInitialFormState] = useState<FormState | null>(null)

  // 選択された加工済み画像のparametersをフォームにロードする
  useEffect(() => {
    const currentTabImages = images.find(img => img.id?.toString() === activeTab)
    const processedImages = getVisibleSimulatedImages(currentTabImages)
    const { selectedProcessedIndex } = getProcessedImageSelectionState(
      activeTab,
      processedImages.length,
      selectedProcessedIndexByTab,
    )

    // 加工済み画像が選択されていて、parametersが存在する場合にフォームにロード
    if (
      typeof selectedProcessedIndex === 'number' &&
      processedImages[selectedProcessedIndex]?.parameters
    ) {
      const params = processedImages[selectedProcessedIndex].parameters!

      // MainAreasから色情報を復元
      const newAreaColors: Record<string, string> = {}
      params.mainAreas.forEach(mainArea => {
        const jpAreaName = API_TO_AREA_NAME_MAP[mainArea.areaName] ?? mainArea.areaName
        if (mainArea.color) {
          newAreaColors[jpAreaName] = mainArea.color
        }
      })
      setAreaColors(newAreaColors)

      // MainAreaMaterialsから素材情報を復元 + active mode を構築
      // (古い simulation 結果には mainAreaMaterials フィールドが無いケースがあるため null-safe で扱う)
      const newAreaMaterials: Record<string, bigint> = {}
      const newAreaSelectionMode: Record<string, 'color' | 'material'> = {}
      ;(params.mainAreaMaterials ?? []).forEach(material => {
        const jpAreaName = API_TO_AREA_NAME_MAP[material.areaName] ?? material.areaName
        if (material.materialId !== 0n) {
          newAreaMaterials[jpAreaName] = material.materialId
          newAreaSelectionMode[jpAreaName] = 'material'
        }
      })
      // 色だけ set されているエリアは mode='color'（素材と排他）
      Object.keys(newAreaColors).forEach(areaLabel => {
        if (!(areaLabel in newAreaSelectionMode)) {
          newAreaSelectionMode[areaLabel] = 'color'
        }
      })
      setAreaMaterials(newAreaMaterials)
      setAreaSelectionMode(newAreaSelectionMode)

      // ベースエリアに統合されたポリゴン領域を復元（issue #2224）
      const newMergedPolygonsByArea = extractMergedPolygonsByArea(params)
      setMergedPolygonsByArea(newMergedPolygonsByArea)

      // SubAreasから座標情報を復元
      const newSubAreas = params.subAreas.map((subArea, index) => {
        const polygonPoints =
          subArea.polygon?.points.map(p => ({
            originalX: p.x,
            originalY: p.y,
          })) ?? []
        const extraPolygons =
          subArea.polygons?.map(poly =>
            poly.points.map(p => ({
              originalX: p.x,
              originalY: p.y,
            })),
          ) ?? []

        const points = polygonPoints.length > 0 ? polygonPoints : (extraPolygons[0] ?? [])
        const polygons = polygonPoints.length > 0 ? extraPolygons : extraPolygons.slice(1)

        return {
          id: -(index + 1), // 負のインデックスで安定したIDを生成
          label: subArea.label || undefined,
          color: subArea.color,
          useDetectedAreaOnly: subArea.useDetectedAreaOnly ?? true, // 検出領域に限定（デフォルト: true）
          points,
          polygons,
        }
      })
      setSubAreas(newSubAreas)

      // ExcludeAreasから座標情報を復元
      const newExcludeAreas = params.excludeAreas.map((excludeArea, index) => ({
        id: -(1000 + index + 1), // SubAreaと重複しない負のインデックス
        points:
          excludeArea.polygon?.points.map(p => ({
            originalX: p.x,
            originalY: p.y,
          })) ?? [],
      }))
      setExcludeAreas(newExcludeAreas)

      // その他のオプションを復元
      setUnifyColor(params.unifyTwoToOne)

      // カスタムエリア・除外エリアのモードは復元時にONにしない
      // ユーザーが明示的にエリアをクリックしたときにモードがONになる

      // 初期状態を保存（変更検知用）
      setInitialFormState({
        areaColors: newAreaColors,
        areaMaterials: newAreaMaterials,
        mergedPolygonsByArea: newMergedPolygonsByArea,
        areaSelectionMode: newAreaSelectionMode,
        subAreas: newSubAreas,
        excludeAreas: newExcludeAreas,
        unifyColor: params.unifyTwoToOne,
      })
    } else {
      // 元画像が選択された場合は初期状態とフォームをクリア
      setInitialFormState(null)
      setAreaColors({})
      setAreaMaterials({})
      setMergedPolygonsByArea({})
      setAreaSelectionMode({})
      setSubAreas([])
      setExcludeAreas([])
      setUnifyColor(false)

      setIsSubAreaModeActive(false)
      setExcludeEnabled(false)
    }
  }, [
    activeTab,
    images,
    selectedProcessedIndexByTab,
    setAreaColors,
    setAreaMaterials,
    setMergedPolygonsByArea,
    setAreaSelectionMode,
    setExcludeAreas,
    setExcludeEnabled,
    setIsSubAreaModeActive,
    setSubAreas,
    setUnifyColor,
  ])

  return { initialFormState }
}

/**
 * フォームが初期状態から変更されたかどうかをチェックするhook
 */
export const useFormChangeDetection = (
  initialFormState: FormState | null,
  areaColors: Record<string, string>,
  subAreas: SubArea[],
  excludeAreas: ExcludeArea[],
  unifyColor: boolean,
  areaMaterials: Record<string, bigint>,
  areaSelectionMode: Record<string, 'color' | 'material'>,
  mergedPolygonsByArea: MergedPolygonsByArea,
) => {
  return useMemo(() => {
    if (!initialFormState) {
      // 初期状態がない場合は常に変更ありとみなす（元画像選択時など）
      return true
    }

    // areaColorsの比較
    if (!deepEqualWithBigInt(areaColors, initialFormState.areaColors)) {
      return true
    }

    // areaMaterialsの比較
    if (!deepEqualWithBigInt(areaMaterials, initialFormState.areaMaterials)) {
      return true
    }

    // 統合ポリゴン（issue #2224）の比較 — 統合の追加・解除も「変更」として検知
    if (!deepEqualWithBigInt(mergedPolygonsByArea, initialFormState.mergedPolygonsByArea)) {
      return true
    }

    // areaSelectionModeの比較 (色 ⇄ 素材の active 切替も「変更」として扱う)
    if (!deepEqualWithBigInt(areaSelectionMode, initialFormState.areaSelectionMode)) {
      return true
    }

    // subAreasの深い比較（座標まで含めて検知）
    if (!deepEqualWithBigInt(subAreas, initialFormState.subAreas)) {
      return true
    }

    // excludeAreasの深い比較（座標まで含めて検知）
    if (!deepEqualWithBigInt(excludeAreas, initialFormState.excludeAreas)) {
      return true
    }

    // オプションの比較
    if (unifyColor !== initialFormState.unifyColor) {
      return true
    }

    return false
  }, [
    initialFormState,
    areaColors,
    subAreas,
    excludeAreas,
    unifyColor,
    areaMaterials,
    areaSelectionMode,
    mergedPolygonsByArea,
  ])
}
